import { FC, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { UprightType } from "../../../../../http/border/types/uprightTypes";
import { useUprightTypesApi } from "../../../../../http/border/useUprightTypes";
import { PaginationQuery } from "../../../../../types";
import Button from "../../../../ui/Button";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import NewUprightType from "./NewUprightType";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const UprightTypes: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const navigate = useNavigate();
  const { systemId } = useParams<{ systemId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "uprightTypes" });
  const aborter = useRef(new AbortController());
  const uprightTypesApi = useUprightTypesApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "uprightTypes");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await uprightTypesApi.updateAdminUprightTypeVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await uprightTypesApi.updateAdminUprightTypeOrder(id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [showNewTypeDialog, setShowNewTypeDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<UprightType>();
  const { accessesAction, accessesDialog } = useEntityAccesses<UprightType>("border.upright_type");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = uprightTypesApi.useAdminUprightTypes(query, aborter.current.signal);

  const detailUrl = (id: string) => `/admin/systems/${systemId}/upright_types/${id}`;

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.uprightTypes.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.uprightTypes.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Button buttonProps={{ onClick: () => setShowNewTypeDialog(true) }}>{i18n.translation.common.createNew}</Button>
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<UprightType>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>{item.name}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => navigate(detailUrl(item.id))}
        rowActions={(item) => [
          getEditAction(i18n, () => navigate(detailUrl(item.id))),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {showNewTypeDialog && (
        <ModalDialog onClose={() => setShowNewTypeDialog(false)} title={i18n.translation.uprightTypes.createNew}>
          <NewUprightType />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.uprightTypes.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                uprightTypesApi
                  .deleteAdminUprightType(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.uprightTypes.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default UprightTypes;
